// src/auth/invitationApi.js — POST /api/v1/auth/invitations/accept.
//
// ⛔ Deliberately a bare `fetch`, NOT apiFetch — the same reasoning as
// `passwordResetApi.js`, for the opposite reason. This route DOES need the
// bearer, but it must not carry anything else about the current session:
//   · no `X-Tenant-Id`: the token names its own target, and a stale active
//     workspace header is at best noise and at worst a refusal about a
//     workspace the invitation has nothing to do with;
//   · no apiFetch 401 handling: a 401 here is a page state (the session died
//     while the invitation was in hand) and the accept page owns it — it keeps
//     the token and offers to sign in again. apiFetch would wipe auth storage
//     and redirect, losing the page and its copy.
//
// ⚠️ The error envelope is FLAT: `reason` is TOP-LEVEL (buildApiError hoists it,
// so callers read `err.code` / `err.reason`), seat numbers under `details`.
import { API_V1, getSessionHeaders } from '../services/api/config.js';
import { buildApiError } from '../context/parseApiError.js';

/**
 * Spend an invitation token.
 *
 * @param {string} token the 43-char single-use handle from the emailed link
 * @returns {Promise<object>} the 200 body: {kind, target_id, role, already_member,
 *   access_token, token_type, tenants[], customers[]}. ⛔ No `is_superadmin`.
 * @throws {Error} carrying `code` (+ `reason`, `details`, `status`), or
 *   `code: 'NETWORK'` on a transport failure.
 */
export async function acceptInvitation(token) {
  let res;
  try {
    res = await fetch(`${API_V1}/auth/invitations/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
        ...getSessionHeaders(),
      },
      body: JSON.stringify({ token }),
    });
  } catch {
    // Transport failure — give it a code so the page branches without
    // string-matching a message.
    const err = new Error('NETWORK');
    err.code = 'NETWORK';
    throw err;
  }
  if (!res.ok) {
    const parsed = await res.json().catch(() => ({}));
    throw buildApiError(parsed, res.status);
  }
  return res.json();
}

// ── In-flight de-duplication ────────────────────────────────────────────
// The token is SINGLE USE: a second POST while the first is in flight makes the
// loser read ALREADY_USED, which the page would then word as "this invitation
// has already been used" on the very request that used it. StrictMode runs every
// effect twice in development, so this is not a hypothetical race.
//
// ⚠️ This covers CONCURRENT calls only — the entry is dropped as soon as the
// promise settles, on purpose (a [Riprova] after a transient failure must reach
// the network). What stops a SECOND call after a successful one is the page's own
// `startedRef` (AcceptInvitationScreen), not this map.
const inFlight = new Map();

export function acceptInvitationOnce(token) {
  const existing = inFlight.get(token);
  if (existing) return existing;
  const promise = acceptInvitation(token).finally(() => { inFlight.delete(token); });
  inFlight.set(token, promise);
  return promise;
}
